import { Box, Flex, Text, Badge } from '@chakra-ui/react';
import { useCallback } from 'react';
import { Conversation } from '@/features/conversations/conversations.types';
import { useWhatsAppEvents } from '@/context/whatsapp-events-context';

interface UserInfoBarProps {
  user: {
    id?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    avatar?: string;
  };
  conversation: Conversation;
  agent: {
    id: string;
    name: string;
    avatar: string;
  };
  agentStatus: 'active' | 'inactive' | 'away';
  onHumanTakeoverOpen: () => void;
  onAITakeoverOpen: () => void;
}

export function UserInfoBar({
  user,
  agentStatus,
  onHumanTakeoverOpen,
  onAITakeoverOpen,
  conversation,
}: UserInfoBarProps) {
  const { isChannelConnected } = useWhatsAppEvents();

  const isCurrentChannelConnected = isChannelConnected(
    conversation?.channel_id || '',
  );

  // Handle badge click based on current status
  const handleBadgeClick = useCallback(() => {
    if (agentStatus === 'active') {
      // If AI is active, human can take over
      onHumanTakeoverOpen();
    } else if (agentStatus === 'inactive') {
      // If agent is inactive (human is handling), AI can take over
      onAITakeoverOpen();
    }
  }, [agentStatus, onHumanTakeoverOpen, onAITakeoverOpen]);

  // Determine if the badge should be clickable
  const isBadgeClickable = true;
  // Format user name
  const userName =
    user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.first_name || user.email || 'Unknown User';
  // TODO: Add last seen time
  const channelName = conversation?.channel_name || 'Unknown Channel';
  return (
    <Flex
      justify="space-between"
      align="center"
      px={6}
      py={4}
      borderBottom="1px"
      borderColor="gray.200"
      bg="white"
    >
      <Box>
        <Text
          fontSize="2xl"
          fontWeight="bold"
          color="black"
          mb={1}
          display="flex"
          alignItems="center"
          gap={1}
          textTransform="capitalize"
        >
          {userName}
          <Box
            as="span"
            width="10px"
            height="10px"
            borderRadius="50%"
            bg={isCurrentChannelConnected ? 'green.500' : 'red.500'}
          />
        </Text>
        <Badge
          colorPalette="purple"
          variant="subtle"
          borderRadius="full"
          px={3}
          py={1}
          textTransform="capitalize"
        >
          {channelName}
        </Badge>
        {(() => {
          // Get phone number from user or wab_chatId_external
          const rawPhone = conversation?.user?.phone_number || conversation?.wab_chatId_external?.replace(/@(c\.us|lid|s\.whatsapp\.net)$/, '');
          // Only show if it looks like a valid phone number (10-14 digits)
          // LIDs are typically 15+ digits and should not be displayed
          const isValidPhone = rawPhone && /^\d{10,14}$/.test(rawPhone);
          return isValidPhone ? (
            <Badge
              colorPalette="gray"
              variant="subtle"
              borderRadius="full"
              px={3}
              py={1}
              ml={2}
            >
              {rawPhone}
            </Badge>
          ) : null;
        })()}
      </Box>

      <Flex align="center" gap={2}>
        <Box
          bg="gray.100"
          px={4}
          py={2}
          borderRadius="full"
          cursor={isBadgeClickable ? 'pointer' : 'default'}
          _hover={isBadgeClickable ? { opacity: 0.8 } : {}}
          onClick={handleBadgeClick}
          position="relative"
        >
          <Badge
            colorPalette="orange"
            variant="solid"
            borderRadius="full"
            px={2}
            py={0.5}
            fontSize="xs"
            position="absolute"
            top="-8px"
            left="-8px"
            transform="rotate(-12deg)"
            zIndex={10}
            boxShadow="md"
          >
            Beta
          </Badge>
          <Text fontSize="sm" fontWeight="medium" color="black">
            AI Agent:{' '}
            <Text
              as="span"
              color={agentStatus === 'active' ? 'green.500' : 'gray.500'}
              fontWeight="medium"
            >
              {agentStatus === 'active' ? 'Active' : 'Inactive'}
            </Text>
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
}
